import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Phone, Mail, Instagram, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { LANDING } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", property_address: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please enter your name and email."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, enquiry_type: "appraisal" });
      setSuccess(true);
      toast.success("Thanks — James will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", property_address: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or call directly.");
    } finally { setLoading(false); }
  };

  return (
    <section
      id="contact"
      data-testid={LANDING.contact}
      className="relative bg-white py-24 md:py-28 lg:py-32"
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-5">Get in touch</div>
            <h2 className="font-display text-[2.4rem] sm:text-[3rem] lg:text-[3.4rem] leading-[1.05] tracking-tight text-[#0E0E0E]">
              Let&rsquo;s talk about <span className="font-display-italic">your <span className="under-yellow">property.</span></span>
            </h2>
            <p className="mt-6 max-w-md text-[16.5px] text-[#4A4A4A] leading-[1.7]">
              No obligation. No pressure. Share a few details and James will be in touch with a market-informed appraisal and a clear next step.
            </p>

            <div className="mt-9 space-y-4">
              <ContactRow icon={Phone} label="Mobile" value="+64 27 214 1475" href="tel:+64272141475" testId={LANDING.footerPhone} />
              <ContactRow icon={Mail} label="Email" value="james@jamestownsendrealestate.co.nz" href="mailto:james@jamestownsendrealestate.co.nz" testId={LANDING.footerEmail} />
              <ContactRow icon={Instagram} label="Instagram" value="@jamestownsend_raywhite" href="https://www.instagram.com/jamestownsend_raywhite/" external testId={LANDING.footerInstagram} />
            </div>
          </div>

          <div className="lg:col-span-7">
            {success ? (
              <div data-testid={LANDING.contactSuccess} className="bg-[#FAFAF7] rounded-sm p-8 md:p-12 border border-[#E6E3D9] anim-fade-up">
                <div className="w-12 h-12 rounded-sm bg-[#FFE512] text-[#0E0E0E] flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h3 className="mt-6 font-display text-3xl md:text-4xl leading-[1.1] tracking-tight text-[#0E0E0E]">
                  Thanks, <span className="font-display-italic">your enquiry is in.</span>
                </h3>
                <p className="mt-4 text-[16px] text-[#4A4A4A] leading-[1.7] max-w-md">
                  James will be in touch shortly to discuss your property. If it&rsquo;s urgent, <a href="tel:+64272141475" className="underline decoration-[#FFE512] decoration-2 underline-offset-2">call 027 214 1475</a>.
                </p>
                <button onClick={() => setSuccess(false)} className="mt-7 btn btn-ghost">
                  Send another enquiry
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form
                data-testid={LANDING.contactForm}
                onSubmit={onSubmit}
                className="bg-[#FAFAF7] rounded-sm p-7 md:p-10 border border-[#E6E3D9] space-y-5"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Name" name="name" value={form.name} onChange={onChange} testId={LANDING.contactName} />
                  <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testId={LANDING.contactEmail} />
                  <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} testId={LANDING.contactPhone} />
                  <Field label="Property address" name="property_address" value={form.property_address} onChange={onChange} testId={LANDING.contactAddress} />
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4A4A4A] mb-2">
                    Message
                  </label>
                  <textarea
                    data-testid={LANDING.contactMessage}
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    placeholder="Selling, buying, appraisal, advice…"
                    className="w-full bg-white border border-[#E6E3D9] rounded-sm px-4 py-3 text-[15.5px] text-[#0E0E0E] focus:border-[#FFE512] focus:ring-2 focus:ring-[#FFE512]/30 outline-none resize-none transition-all placeholder:text-[#888880]"
                  />
                </div>

                <div className="pt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <p className="text-[12px] text-[#888880] max-w-xs leading-relaxed">
                    Your details stay between us, used only to respond to your enquiry.
                  </p>
                  <button
                    type="submit"
                    data-testid={LANDING.contactSubmit}
                    disabled={loading}
                    className="btn btn-yellow disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? "Sending…" : "Send enquiry"}
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4A4A4A] mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full bg-white border border-[#E6E3D9] rounded-sm px-4 py-3 text-[15.5px] text-[#0E0E0E] focus:border-[#FFE512] focus:ring-2 focus:ring-[#FFE512]/30 outline-none transition-all"
      />
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, external, testId }) {
  return (
    <a
      href={href}
      data-testid={testId}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="flex items-center gap-3.5 group"
    >
      <span className="w-10 h-10 rounded-sm bg-white border border-[#E6E3D9] flex items-center justify-center text-[#0E0E0E] group-hover:bg-[#FFE512] group-hover:border-[#FFE512] transition-colors duration-300 shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#888880]">{label}</div>
        <div className="mt-0.5 font-display text-[15.5px] text-[#0E0E0E] truncate">{value}</div>
      </div>
    </a>
  );
}
